import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, vModelText as _vModelText, createElementVNode as _createElementVNode, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "space-y-4"
}
const _hoisted_2 = { class: "flex rounded bg-gray-100 text-gray-400 focus-within:text-gray-600" }
const _hoisted_3 = ["placeholder"]
const _hoisted_4 = { class: "flex" }
const _hoisted_5 = { class: "flex flex-col items-center justify-center space-y-4 pr-2" }
const _hoisted_6 = { class: "flex flex-1 flex-col space-y-1" }
const _hoisted_7 = { class: "flex flex-shrink flex-col sm:w-auto" }
const _hoisted_8 = { class: "flex grow items-center" }
const _hoisted_9 = { class: "w-full rotate-90 transform text-center text-gray-500" }
const _hoisted_10 = { class: "flex grow items-center" }
const _hoisted_11 = { class: "w-full rotate-90 transform text-center text-gray-500" }
const _hoisted_12 = { class: "flex px-0 md:px-14" }
const _hoisted_13 = { class: "flex space-x-4" }
const _hoisted_14 = { class: "flex items-center text-xs text-blue-500" }
const _hoisted_15 = { class: "flex items-center text-xs text-orange-500" }
const _hoisted_16 = { class: "ml-auto text-sm text-gray-500" }
const _hoisted_17 = {
  key: 1,
  class: "text-danger-700"
}

import { ref, computed, onMounted, onUnmounted, PropType } from 'vue';
import store from '@/store';
import { PlayIcon, PauseIcon, ExclamationCircleIcon, SearchIcon } from '@heroicons/vue/solid';

import { CallPlayerBullets } from '@/views/pages/call/components';
import { VaultService } from '@/services';
import { getAudioFormat } from '@/utils/audio';
import { formatSecToTime } from '@/utils/datetime';
import {
    CallPlayerMutations,
    CallType,
    CallBulletType,
    CallParticipantType,
    CallModel,
} from '@/types';
import debounce from 'debounce';
import WaveSurfer from 'wavesurfer.js';
import MultiCanvas from 'wavesurfer.js/src/drawer.multicanvas';
import AnalyticsService from '@/services/analytics/AnalyticsService';


export default /*@__PURE__*/_defineComponent({
  __name: 'CallPlayer',
  props: {
    call: {
        type: Object as PropType<CallModel>,
        required: true,
    },
},
  setup(__props) {



const waveInstance = ref<WaveSurfer | null>(null);
const audioData = ref<string | null>(null);
const audioElement = ref<HTMLElement | null>(null);
const playerReady = ref(false);
const playerError = ref(null);
const callDuration = ref(0);
const secondsCurrent = ref(0);
const loadingPercentage = ref(0);
const searchWord = ref('');

const isPlaying = computed(() => (playerReady.value ? waveInstance.value?.isPlaying() : false));
const playbackRate = computed(() => waveInstance.value?.getPlaybackRate());

// Fetch Audio File
const getAudioFile = async () => {
    if (!__props.call?.audioFilePath) return;

    try {
        const { data } = await VaultService.getCallAudio(__props.call.id);
        audioData.value = URL.createObjectURL(
            new Blob([data], { type: getAudioFormat(__props.call.audioFilePath) }),
        );
        initAudioPlayer();
    } catch (error) {
        console.error('Error fetching audio file:', error);
    }
};

MultiCanvas.prototype.drawBars = function (peaks, channelIndex, start, end) {
    return this.prepareDraw(
        peaks,
        channelIndex,
        start,
        end,
        ({ absmax, hasMinVals, height, offsetY, peaks }) => {
            if (start === undefined) return;

            const barWidth = this.params.barWidth || 3;
            const barGap = this.params.barGap || 3;
            const pixelRatio = this.params.pixelRatio || 1;
            const bar = barWidth * pixelRatio;
            const peakIndexScale = hasMinVals ? 2 : 1;
            const length = peaks.length / peakIndexScale;
            const gap =
                this.params.barGap === null
                    ? Math.max(pixelRatio, ~~(bar / 2))
                    : Math.max(pixelRatio, barGap * pixelRatio);
            const step = bar + gap;
            const gapBetween = 2;
            const scale = length / this.width;

            for (let i = start; i < end; i += step) {
                const peakIndex = Math.floor(i * scale * peakIndexScale);
                const peak = peaks[peakIndex] || 0;
                const rectWidth = bar + this.halfPixel;
                let rectHeight = Math.abs(Math.round((peak / absmax) * (height - gapBetween)));
                if (rectHeight && this.params.barMinHeight && rectHeight < this.params.barMinHeight)
                    rectHeight = this.params.barMinHeight;
                const x = i + this.halfPixel;
                const y = offsetY
                    ? __props.call.type === CallType.INBOUND
                        ? height - rectHeight - gapBetween
                        : height + gapBetween
                    : __props.call.type === CallType.INBOUND
                      ? height + gapBetween
                      : height - rectHeight - gapBetween;
                this.fillRect(x, y, rectWidth, rectHeight, this.barRadius, channelIndex);
            }
        },
        0,
        0,
    );
};

const initAudioPlayer = () => {
    if (!audioElement.value || !audioData.value) return;

    let waveColor: CanvasGradient | string = '#3b82f6';
    if (__props.call?.type !== CallType.INTERNAL) {
        const ctx = document.createElement('canvas').getContext('2d');
        if (ctx) {
            const gradient = ctx.createLinearGradient(0, 0, 0, 150);
            gradient.addColorStop(0, 'rgb(59, 130, 246)');
            gradient.addColorStop(1, 'rgb(59, 130, 246)');
            gradient.addColorStop(1, 'rgb(168, 85, 247)');
            waveColor = gradient;
        }
    }

    waveInstance.value = WaveSurfer.create({
        container: audioElement.value,
        waveColor,
        progressColor: '#adb5bd',
        cursorColor: '#adb5bd',
        barWidth: 3,
        barGap: 3,
        barRadius: 1,
        barMinHeight: 3,
        height: 75,
        normalize: true,
        hideScrollbar: true,
        splitChannels: true,
        skipLength: 10,
        renderer: MultiCanvas,
    });

    waveInstance.value.load(audioData.value);

    waveInstance.value.on('ready', () => {
        playerReady.value = true;
        callDuration.value = waveInstance.value?.getDuration() ?? 0;
    });

    waveInstance.value.on('audioprocess', (audioProcess) => {
        secondsCurrent.value = parseFloat(audioProcess.toFixed(2));
    });

    waveInstance.value.on('loading', (percentage) => {
        loadingPercentage.value = parseFloat(percentage.toFixed(2));
    });

    waveInstance.value.on('error', (error) => {
        loadingPercentage.value = 0;
        playerError.value = error;
    });

    waveInstance.value.on('seek', (seek) => {
        secondsCurrent.value = (waveInstance.value?.getDuration() ?? 0) * seek;
    });
};

// Play / Pause
const playPause = () => {
    if (!waveInstance.value) {
        return;
    }

    const playStatusTracking = waveInstance.value.isPlaying() ? 'PauseCallAudio' : 'PlayCallAudio';

    waveInstance.value.playPause();
    void AnalyticsService.trackingAction(playStatusTracking);
};

// Change Speed
const changeSpeed = () => {
    const speeds = [1, 1.1, 1.2];
    const currentRate = waveInstance.value?.getPlaybackRate() ?? 1;
    waveInstance.value?.setPlaybackRate(speeds[(speeds.indexOf(currentRate) + 1) % speeds.length]);
};

const handlePlaySnippet = (start: number, end: number) => {
    if (waveInstance.value) waveInstance.value.play(start, end);
};

// Lifecycle Hooks
onMounted(async () => {
    await getAudioFile();
});
onUnmounted(() => {
    if (waveInstance.value) {
        waveInstance.value.destroy();
        waveInstance.value = null;
    }
});

return (_ctx: any,_cache: any) => {
  const _component_UiButton = _resolveComponent("UiButton")!
  const _component_UiPanel = _resolveComponent("UiPanel")!
  const _directive_tippy = _resolveDirective("tippy")!

  return (_openBlock(), _createBlock(_component_UiPanel, null, {
    default: _withCtx(() => [
      (!playerError.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createElementVNode("div", _hoisted_2, [
              _createVNode(_unref(SearchIcon), {
                class: "my-2 ml-4 h-6 w-6",
                "aria-hidden": "true"
              }),
              _withDirectives(_createElementVNode("input", {
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchWord).value = $event)),
                type: "search",
                autocomplete: "off",
                placeholder: _ctx.$t('core.actions.Search'),
                class: "grow border-transparent bg-transparent px-4 py-2 text-base text-gray-800 placeholder-gray-500 focus:border-transparent focus:placeholder-gray-400 focus:outline-none focus:ring-0",
                onInput: _cache[1] || (_cache[1] = ($event: any) => (
                        _unref(debounce)(
                            () => _unref(store).commit(_unref(CallPlayerMutations).SET_SEARCH_QUERY, searchWord.value),
                            1000,
                        )()
                    ))
              }, null, 40, _hoisted_3), [
                [_vModelText, searchWord.value]
              ])
            ]),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                _withDirectives((_openBlock(), _createBlock(_component_UiButton, {
                  variant: "primary",
                  "text-variant": "white",
                  "no-padding": "",
                  "no-rounded": "",
                  class: "h-10 w-10 rounded-full",
                  onClick: playPause
                }, {
                  default: _withCtx(() => [
                    (!isPlaying.value)
                      ? (_openBlock(), _createBlock(_unref(PlayIcon), {
                          key: 0,
                          class: "text-lg text-white"
                        }))
                      : (_openBlock(), _createBlock(_unref(PauseIcon), {
                          key: 1,
                          class: "text-lg text-white"
                        }))
                  ]),
                  _: 1
                })), [
                  [_directive_tippy, _ctx.$t('call.view.player.PlayPause')]
                ]),
                _withDirectives((_openBlock(), _createBlock(_component_UiButton, {
                  variant: "primary",
                  "text-variant": "white",
                  "no-padding": "",
                  class: "h-6 w-6 text-sm",
                  onClick: changeSpeed
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("span", null, _toDisplayString(playbackRate.value) + "x", 1)
                  ]),
                  _: 1
                })), [
                  [_directive_tippy, _ctx.$t('call.view.player.ChangeSpeed')]
                ])
              ]),
              _createElementVNode("div", _hoisted_6, [
                _createVNode(_unref(CallPlayerBullets), {
                  call: __props.call,
                  callDuration: callDuration.value,
                  bulletType: _unref(CallBulletType).registered,
                  participantType: 
                            __props.call.type == _unref(CallType).INTERNAL
                                ? _unref(CallParticipantType).AgentA
                                : _unref(CallParticipantType).Agent
                        ,
                  onPlaySnippet: handlePlaySnippet
                }, null, 8, ["call", "callDuration", "bulletType", "participantType"]),
                _createVNode(_unref(CallPlayerBullets), {
                  call: __props.call,
                  callDuration: callDuration.value,
                  bulletType: _unref(CallBulletType).matched,
                  participantType: 
                            __props.call.type == _unref(CallType).INTERNAL
                                ? _unref(CallParticipantType).AgentA
                                : _unref(CallParticipantType).Agent
                        ,
                  onPlaySnippet: handlePlaySnippet
                }, null, 8, ["call", "callDuration", "bulletType", "participantType"]),
                _createElementVNode("div", {
                  ref_key: "audioElement",
                  ref: audioElement
                }, null, 512),
                _createVNode(_unref(CallPlayerBullets), {
                  call: __props.call,
                  callDuration: callDuration.value,
                  bulletType: _unref(CallBulletType).matched,
                  participantType: 
                            __props.call.type == _unref(CallType).INTERNAL
                                ? _unref(CallParticipantType).AgentB
                                : _unref(CallParticipantType).Client
                        ,
                  onPlaySnippet: handlePlaySnippet
                }, null, 8, ["call", "callDuration", "bulletType", "participantType"]),
                _createVNode(_unref(CallPlayerBullets), {
                  call: __props.call,
                  callDuration: callDuration.value,
                  bulletType: _unref(CallBulletType).registered,
                  participantType: 
                            __props.call.type == _unref(CallType).INTERNAL
                                ? _unref(CallParticipantType).AgentB
                                : _unref(CallParticipantType).Client
                        ,
                  onPlaySnippet: handlePlaySnippet
                }, null, 8, ["call", "callDuration", "bulletType", "participantType"])
              ]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("div", _hoisted_9, _toDisplayString(_ctx.$t(
                                    'call.view.participant.' +
                                        (__props.call.type === _unref(CallType).INTERNAL
                                            ? _unref(CallParticipantType).AgentA.toLowerCase()
                                            : _unref(CallParticipantType).Agent.toLowerCase()),
                                )), 1)
                ]),
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, _toDisplayString(_ctx.$t(
                                    'call.view.participant.' +
                                        (__props.call.type === _unref(CallType).INTERNAL
                                            ? _unref(CallParticipantType).AgentB.toLowerCase()
                                            : _unref(CallParticipantType).Client.toLowerCase()),
                                )), 1)
                ])
              ])
            ]),
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("div", _hoisted_13, [
                _createElementVNode("div", _hoisted_14, [
                  _cache[2] || (_cache[2] = _createElementVNode("div", { class: "group mr-1 h-4 w-4 rounded-full bg-blue-500/50 p-0" }, null, -1)),
                  _createElementVNode("div", null, _toDisplayString(_ctx.$t('call.audioPlayer.label.words')), 1)
                ]),
                _createElementVNode("div", _hoisted_15, [
                  _cache[3] || (_cache[3] = _createElementVNode("div", { class: "group mr-1 h-4 w-4 rounded-full bg-orange-500/50 p-0" }, null, -1)),
                  _createElementVNode("div", null, _toDisplayString(_ctx.$t('call.audioPlayer.label.matchedWords')), 1)
                ])
              ]),
              _createElementVNode("div", _hoisted_16, _toDisplayString(_unref(formatSecToTime)(secondsCurrent.value)) + " / " + _toDisplayString(_unref(formatSecToTime)(callDuration.value)), 1)
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_17, [
            _createVNode(_unref(ExclamationCircleIcon), { class: "inline h-6 w-6" }),
            _createTextVNode(" " + _toDisplayString(playerError.value), 1)
          ]))
    ]),
    _: 1
  }))
}
}

})